import { useEffect, useMemo, useState } from 'react';
import { OfficeTask } from './useOfficeEngine';
import { PERSONAS } from './office-config';

/* ============================================================
   剧场引擎 — 把真实任务状态翻译成"具象化办公"演出节拍
   原则：动画节拍挂在真实后台节点上，绝不撒谎——
   · dispatching/running 前几秒演"经理收令→交办"，之后停在"忙碌"循环，任务跑多久演多久
   · 只有后台真的 completed 才演"提交→经理审阅→呈报用户"
   · failed 演"经理致歉"
   ============================================================ */

export type Beat =
  | 'idle'        // 无任务
  | 'receive'     // 经理收到领导指示
  | 'assign'      // 经理走向执行人交办
  | 'working'     // 执行人忙碌（循环，等真实后台）
  | 'submit'      // 执行人完成，向经理提交
  | 'review'      // 经理审阅
  | 'deliver'     // 经理向领导呈报
  | 'done'        // 演出收尾（成果可查看）
  | 'apologize'   // 失败：经理出面致歉
  | 'failed';     // 失败收尾

const RECEIVE_MS = 2400;
const ASSIGN_MS = 2600;
const SUBMIT_MS = 2000;
const REVIEW_MS = 2600;
const DELIVER_MS = 2400;
const APOLOGIZE_MS = 6000;

export function beatOf(task: OfficeTask, now: number): Beat {
  if (task.status === 'failed') {
    // 历史失败记录可能没有 finishedAt；旧逻辑每次都用 now，导致“领导抱歉”永久不消失。
    const dt = now - (task.finishedAt || task.startedAt);
    return dt < APOLOGIZE_MS ? 'apologize' : 'failed';
  }
  if (task.status === 'completed') {
    const dt = now - (task.finishedAt || now);
    if (dt < SUBMIT_MS) return 'submit';
    if (dt < SUBMIT_MS + REVIEW_MS) return 'review';
    if (dt < SUBMIT_MS + REVIEW_MS + DELIVER_MS) return 'deliver';
    return 'done';
  }
  // dispatching / running：开场两拍后停在忙碌
  const dt = now - task.startedAt;
  if (dt < RECEIVE_MS) return 'receive';
  if (dt < RECEIVE_MS + ASSIGN_MS) return 'assign';
  return 'working';
}

/** 该节拍是否还会随时间推进（决定要不要继续 tick） */
const TRANSIENT: Beat[] = ['receive', 'assign', 'submit', 'review', 'deliver', 'apologize'];

export interface TheaterState {
  /** localId -> 当前节拍 */
  beats: Record<string, Beat>;
  /** 每部门取"最值得演"的一个任务（进行中优先，其次最近收尾的） */
  staged: Record<string, { task: OfficeTask; beat: Beat }>;
  now: number;
}

export function useTheater(tasks: OfficeTask[]): TheaterState {
  const [now, setNow] = useState(() => Date.now());

  // 有瞬态节拍时快速 tick，否则慢心跳（保证 done/failed 收尾能到达）
  useEffect(() => {
    const anyTransient = tasks.some((t) => TRANSIENT.includes(beatOf(t, Date.now())));
    const anyAlive = tasks.some((t) => t.status === 'running' || t.status === 'dispatching');
    // 刚收尾的任务还要靠心跳把"呈报"演出撤下（30s 后回归平静）
    const anyRecentFinal = tasks.some((t) => (t.status === 'completed' || t.status === 'failed') && Date.now() - (t.finishedAt || 0) < 35000);
    if (!anyTransient && !anyAlive && !anyRecentFinal) return undefined;
    const timer = setInterval(() => setNow(Date.now()), anyTransient ? 300 : 1500);
    return () => clearInterval(timer);
  }, [tasks, now]);

  return useMemo(() => {
    const beats: Record<string, Beat> = {};
    for (const t of tasks) beats[t.localId] = beatOf(t, now);
    const staged: Record<string, { task: OfficeTask; beat: Beat }> = {};
    const score = (b: Beat) => (b === 'idle' ? 0 : b === 'done' ? 1 : b === 'failed' ? 1 : TRANSIENT.includes(b) ? 3 : 2);
    for (const t of tasks) {
      const b = beats[t.localId];
      const cur = staged[t.deptId];
      if (!cur || score(b) > score(cur.beat) || (score(b) === score(cur.beat) && t.startedAt > cur.task.startedAt)) {
        staged[t.deptId] = { task: t, beat: b };
      }
    }
    // done/failed 的收尾只保留 30 秒，之后回归平静
    for (const k of Object.keys(staged)) {
      const s = staged[k];
      if ((s.beat === 'done' || s.beat === 'failed') && now - (s.task.finishedAt || 0) > 30000) delete staged[k];
    }
    return { beats, staged, now };
  }, [tasks, now]);
}

/* ---------------- 节拍台词（气泡/群聊共用） ---------------- */

export interface TheaterLine {
  role: 'manager' | 'assistant' | 'system' | 'leader';
  name: string;
  text: string;
  /** 任务群中该条工作记录的显示时间。 */
  timestamp: number;
  /** 附件卡（呈报时带成果入口） */
  attach?: 'result';
}

/** 执行者称呼：选了具体智能体=该智能体，否则=部门助理 */
export function executorName(task: OfficeTask, deptId: string): string {
  const p = PERSONAS[deptId];
  if (task.kind === 'workshop' || task.kind === 'ppt' || task.kind === 'design') return p?.assistant || '部门助理';
  return task.agentName;
}

/** 某节拍下，气泡该说什么（单句，给场景演出用） */
export function beatBubble(task: OfficeTask, deptId: string, beat: Beat): { who: 'manager' | 'executor'; text: string } | null {
  const p = PERSONAS[deptId];
  const mgr = p?.manager || 'AI助理';
  const exec = executorName(task, deptId);
  switch (beat) {
    case 'receive': return { who: 'manager', text: '收到领导指示，马上安排！' };
    case 'assign': return { who: 'manager', text: `${exec}，这项工作交给你，抓紧办。` };
    case 'working': return { who: 'executor', text: task.stepText || '正在处理…' };
    case 'submit': return { who: 'executor', text: `${mgr}，任务办完了，请您审核。` };
    case 'review': return { who: 'manager', text: '我审阅一下…嗯，质量可以。' };
    case 'deliver': return { who: 'manager', text: '领导，任务已完成，成果请您过目。' };
    case 'done': return { who: 'manager', text: '成果已呈报，请领导查阅。' };
    case 'apologize': return { who: 'manager', text: `领导抱歉，这项任务遇到了问题：${(task.error || '执行失败').slice(0, 40)}` };
    default: return null;
  }
}

/** 群聊过程层：截至当前节拍，应当已出现的消息序列 */
export function chatScript(task: OfficeTask, deptId: string, beat: Beat): TheaterLine[] {
  const p = PERSONAS[deptId];
  const mgr = p?.manager || 'AI助理';
  const exec = executorName(task, deptId);
  const startedAt = task.startedAt;
  const finishedAt = task.finishedAt;
  // 任务很快完成时，前置办理记录不能晚于真实完成时间。
  const duringTask = (offset: number) => finishedAt ? Math.min(startedAt + offset, finishedAt) : startedAt + offset;
  const order: Beat[] = ['receive', 'assign', 'working', 'submit', 'review', 'deliver', 'done'];
  const okIdx = beat === 'apologize' || beat === 'failed' ? -1 : order.indexOf(beat);
  const lines: TheaterLine[] = [
    { role: 'leader', name: '领导', text: task.command, timestamp: startedAt },
  ];
  const failed = beat === 'apologize' || beat === 'failed';
  const reach = (b: Beat) => (failed ? ['receive', 'assign', 'working'].includes(b) : okIdx >= order.indexOf(b));
  if (reach('receive')) lines.push({ role: 'manager', name: mgr, text: '收到领导指示，马上安排！', timestamp: duringTask(800) });
  if (reach('assign')) lines.push({ role: 'manager', name: mgr, text: `@${exec} 领导交办：「${task.command.slice(0, 50)}${task.command.length > 50 ? '…' : ''}」，你来主办，尽快出成果。`, timestamp: duringTask(RECEIVE_MS) });
  if (reach('working')) lines.push({ role: 'assistant', name: exec, text: '收到，马上办！', timestamp: duringTask(RECEIVE_MS + ASSIGN_MS) });
  if (reach('working') && !failed && okIdx <= order.indexOf('working') && task.stepText) {
    lines.push({ role: 'assistant', name: exec, text: `（进展）${task.stepText}`, timestamp: duringTask(RECEIVE_MS + ASSIGN_MS + 1000) });
  }
  if (failed) {
    const failedAt = finishedAt || duringTask(RECEIVE_MS + ASSIGN_MS + 1000);
    lines.push({ role: 'assistant', name: exec, text: `报告${mgr}，执行遇到问题：${task.error || '执行失败'}`, timestamp: failedAt });
    lines.push({ role: 'manager', name: mgr, text: '@领导 很抱歉，这项任务执行失败了，您可以调整要求后让我们重试。', timestamp: failedAt });
    return lines;
  }
  const submittedAt = finishedAt || duringTask(RECEIVE_MS + ASSIGN_MS + 1000);
  if (reach('submit')) lines.push({ role: 'assistant', name: exec, text: `${mgr}，任务完成，成果已提交，请您审核。`, timestamp: submittedAt, attach: task.resultFileName ? 'result' : undefined });
  if (reach('review')) lines.push({ role: 'manager', name: mgr, text: '我先审阅一下。', timestamp: submittedAt + SUBMIT_MS });
  if (reach('deliver')) lines.push({ role: 'manager', name: mgr, text: '@领导 已审核通过，成果请您过目。', timestamp: submittedAt + SUBMIT_MS + REVIEW_MS, attach: 'result' });
  return lines;
}
